import { select, Selection, EnterElement, ScaleLinear } from "d3"
import { Renderable } from "../../Types/Renderable"
import { ThresholdLineConfig } from "../../Types/Graph"

export type D3ThresholdLinesConfig = {
	yScale: ScaleLinear<any, any, any>
	width: number
	thresholds: ThresholdLineConfig[]
}

// Draws one horizontal reference line per goal value at yScale(value). Deliberately
// mirrors D3HorizontalLines (the grid lines) so it scales/positions identically;
// an empty list draws nothing.
export class D3ThresholdLines implements Renderable {
	private root: SVGGElement
	private config: D3ThresholdLinesConfig
	private className = "d3-threshold-lines"

	constructor(root: SVGGElement, config: D3ThresholdLinesConfig) {
		this.root = root
		this.config = config

		const rootNode = select(root)
			.append("g")
			.attr("class", this.className)
			.node()

		if (rootNode != null) {
			this.root = rootNode
		}

		this.render()
	}

	public updateConfig = (config: D3ThresholdLinesConfig) => {
		this.config = config
		this.render()
	}

	public render = () => {
		select(this.root)
			.selectAll("line")
			.data(this.config.thresholds)
			.join(
				newElements => this.enter(newElements),
				updatedElements => this.update(updatedElements),
				exitedElements => exitedElements.remove()
			)
	}

	// A line outside the graph's current y range is hidden rather than drawn over the
	// graphs and controls around it.
	private isInRange = (threshold: ThresholdLineConfig) => {
		const [a, b] = this.config.yScale.domain()
		return threshold.value >= Math.min(a, b) && threshold.value <= Math.max(a, b)
	}

	private enter = (newElements: Selection<EnterElement, ThresholdLineConfig, any, any>): Selection<any, any, any, any> => {
		return newElements.append("line")
			.attr("display", threshold => this.isInRange(threshold) ? null : "none")
			.attr("x1", 0)
			.attr("x2", this.config.width)
			.attr("y1", threshold => this.config.yScale(threshold.value))
			.attr("y2", threshold => this.config.yScale(threshold.value))
			.attr("stroke", threshold => threshold.color ?? "var(--color-chart-grid)")
			.attr("stroke-width", 1.5)
			.attr("stroke-dasharray", threshold => (threshold.isDashed ? "5,4" : "none"))
	}

	private update = (updatedLines: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		return updatedLines
			.attr("display", (threshold: ThresholdLineConfig) => this.isInRange(threshold) ? null : "none")
			.attr("x2", this.config.width)
			.attr("y1", (threshold: ThresholdLineConfig) => this.config.yScale(threshold.value))
			.attr("y2", (threshold: ThresholdLineConfig) => this.config.yScale(threshold.value))
			.attr("stroke", (threshold: ThresholdLineConfig) => threshold.color ?? "var(--color-chart-grid)")
			.attr("stroke-dasharray", (threshold: ThresholdLineConfig) => (threshold.isDashed ? "5,4" : "none"))
	}
}
